import React, { useCallback, useMemo, useState } from 'react';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { useStepUpAuthContext } from '../../../../../global/step-up-authentication';
import { getMfaEnrollments, removeMfaEnrollment } from './api';
import { useFlexLayout, useTable, useSortBy } from 'react-table';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { createTypeColumnDefinition } from './enrollments-table/type-column';
import { createEnrollmentColumnDefinition } from './enrollments-table/enrollments-column';
import { createCreateDateColumnDefinition } from './enrollments-table/create-date-column';
import { createLastAuthDateColumnDefinition } from './enrollments-table/last-auth-date-column';
import { createActionButtonColumnDefinition } from './enrollments-table/action-button-column';
import { getIsVoice } from '../../../../../global/step-up-authentication/helpers';
import { AddMfaEnrollment } from './add-mfa-enrollment';
import { messages } from './messages';
import style from './security.module.less';

const addUserEnrollments = (userEnrollments) => {
  return userEnrollments.reduce((acc, cur) => {
    // filter out voice
    if (getIsVoice(cur)) {
      return acc;
    }

    acc.push(cur);
    return acc;
  }, []);
};

export const CurrentEnrollments = () => {
  const { accessToken, detectAndHandleAuthTokenExpirationError, mfaEnrollments } = useStepUpAuthContext();
  const [enrollments, setEnrollments] = useState(mfaEnrollments);
  const [error, setError] = useAsyncSafeState(null);
  const resetError = useCallback(() => setError(null), [setError]);
  const hydratedEnrollmentOptions = useMemo(() => addUserEnrollments(enrollments), [enrollments]);

  const onNewEnrollmentAdded = async () => {
    try {
      const resData = await getMfaEnrollments({ mfaToken: accessToken });
      setEnrollments(resData);
    } catch (err) {
      detectAndHandleAuthTokenExpirationError(err);
      if (err?.response?.data) {
        setError(err.response.data);
      } else {
        setError(err);
      }
    }
  };

  const onRemoveEnrollment = async (enrollmentId) => {
    try {
      await removeMfaEnrollment({
        enrollmentId,
        mfaToken: accessToken,
      });
      const resData = await getMfaEnrollments({ mfaToken: accessToken });
      setEnrollments(resData);
    } catch (err) {
      detectAndHandleAuthTokenExpirationError(err);
      if (err?.response?.data) {
        setError(err.response.data);
      } else {
        setError(err);
      }
    }
  };

  const columns = useMemo(
    () => [
      createTypeColumnDefinition(),
      createEnrollmentColumnDefinition(),
      createCreateDateColumnDefinition(),
      createLastAuthDateColumnDefinition(),
      createActionButtonColumnDefinition(),
    ],
    [],
  );

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: hydratedEnrollmentOptions,
      onRemoveEnrollment,
      disableSortRemove: true,
      hasOneEnrollment: hydratedEnrollmentOptions.length === 1,
    },
    useSortBy,
    useFlexLayout,
  );

  return (
    <FlexColumn gap={Gap.Space8}>
      {error && (
        <BannerMessageV2
          content={<Text textType={TextType.Paragraph} value={error?.message || messages.defaultError} />}
          onClose={resetError}
          status={BannerStatus.Error}
        />
      )}
      <AddMfaEnrollment onSuccess={onNewEnrollmentAdded} />
      <ReactTableWrapper
        className={style.table}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </FlexColumn>
  );
};
